import React, { useEffect, useId, useState } from 'react';
import Dialog from '../common/Dialog';
import { CANCEL_REASONS, CancelCode } from '../../dtos/scheduling/schedule.dto';

export interface CancelShiftDialogProps {
  open: boolean;
  /** "1 shift", "Mon 12 · 7a–7p", "12 shifts" … */
  summary: string;
  /** Offer "this and following" (a series occurrence) */
  allowFollowing?: boolean;
  defaultCode?: CancelCode;
  busy?: boolean;
  onConfirm: (code: CancelCode, note: string, scope: 'this' | 'following') => void | Promise<void>;
  onCancel: () => void;
}

/** SCH-01 soft cancel: a reason code (Other needs a note). The shift stays on record. */
const CancelShiftDialog: React.FC<CancelShiftDialogProps> = ({ open, summary, allowFollowing, defaultCode, busy, onConfirm, onCancel }) => {
  const id = useId();
  const [code, setCode] = useState<CancelCode | ''>(defaultCode || '');
  const [note, setNote] = useState('');
  const [scope, setScope] = useState<'this' | 'following'>('this');
  useEffect(() => {
    if (open) { setCode(defaultCode || ''); setNote(''); setScope('this'); }
  }, [open, defaultCode]);
  const valid = Boolean(code) && (code !== 'OTHER' || note.trim().length > 0);

  return (
    <Dialog open={open} onClose={onCancel} labelledBy={`${id}-title`}>
      <h2 id={`${id}-title`} className="text-lg font-semibold text-gray-900">Cancel shift</h2>
      <p className="mt-1 text-sm text-gray-600">{summary}. The shift stays on record with the reason; PA usage is not affected.</p>
      <fieldset className="mt-4">
        <legend className="mb-2 text-sm font-medium text-gray-700">Reason</legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {CANCEL_REASONS.map((r) => (
            <label key={r.code} className={`flex min-h-[44px] cursor-pointer items-center gap-2 rounded-md border px-3 text-sm ${code === r.code ? 'border-slate-800 bg-slate-50' : 'border-gray-300'}`}>
              <input type="radio" name={`${id}-code`} value={r.code} checked={code === r.code} onChange={() => setCode(r.code)} className="h-4 w-4" />
              {r.label}
            </label>
          ))}
        </div>
      </fieldset>
      <label htmlFor={`${id}-note`} className="mt-4 block text-sm font-medium text-gray-700">
        Note{code === 'OTHER' ? <span className="text-red-600"> *</span> : ' (optional)'}
      </label>
      <textarea id={`${id}-note`} rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-base md:text-sm" />
      {allowFollowing && (
        <fieldset className="mt-4">
          <legend className="mb-2 text-sm font-medium text-gray-700">Cancel</legend>
          <div className="flex flex-wrap gap-2">
            {([['this', 'Only this shift'], ['following', 'This and all following (ends the series)']] as ['this' | 'following', string][]).map(([v, label]) => (
              <label key={v} className={`flex min-h-[44px] cursor-pointer items-center gap-2 rounded-md border px-3 text-sm ${scope === v ? 'border-slate-800 bg-slate-50' : 'border-gray-300'}`}>
                <input type="radio" name={`${id}-scope`} checked={scope === v} onChange={() => setScope(v)} className="h-4 w-4" />{label}
              </label>
            ))}
          </div>
        </fieldset>
      )}
      <div className="mt-6 flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="min-h-[44px] rounded-md border border-gray-300 px-4 text-sm font-medium text-gray-700">Keep shift</button>
        <button
          type="button"
          disabled={!valid || busy}
          onClick={() => code && onConfirm(code, note.trim(), scope)}
          className="min-h-[44px] rounded-md bg-red-600 px-4 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
        >
          {busy ? 'Cancelling…' : 'Cancel shift'}
        </button>
      </div>
    </Dialog>
  );
};

export default CancelShiftDialog;
